/**
 * VatReturnView — reine Präsentation der UStVA-Vorschau-Kennzahlen (M8b).
 *
 * Props-getrieben, KEIN Firebase/State: rendert die `VatReturnFigures` gruppiert
 * (Ausgang steuerpflichtig / steuerfrei+§13b-Ausgang / Eingang §13b+Erwerb / Vorsteuer)
 * mit prominenter Zahllast (KZ 83), dem fail-closed `unclassifiedIncome`-Bucket als
 * sichtbarer Warnung, dem A1-Draft-Hinweis, dem Soll-Fallback-Datum-Hinweis sowie dem
 * §19-Sonderfall (reduzierte Pflicht-Sicht bei §13b/Erwerb, sonst „keine UStVA").
 *
 * Beträge kommen als Integer-Cents herein; Darstellungsrundung passiert EINMALIG hier
 * am Render-Rand (§5.8): Bemessungsgrundlagen auf volle Euro (ELSTER-Konvention,
 * Centbeträge ABSCHNEIDEN Richtung 0 — identisch zur Export-Kürzung, §6.2/M-U12, damit
 * Vorschau und erzeugte XML-Datei nie um ±1 € differieren), Steuer-/Zahllast mit Cent.
 *
 * Haftungsausschluss + Scope-Liste (§5.6) sind PFLICHT und persistent gerendert.
 * OP-4-Freigabe durch KI-Jurist erteilt 2026-07-18 (jurist-ustva-disclaimer-final.md);
 * anwaltliche Nachprüfung bei 10k€/Mon.
 *
 * @module VatReturnView
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconWarning,
  IconInfo,
  IconChevronDown,
  IconChevronRight,
} from '../../icons';

import { VatReturnFigures } from '../../utils/vatReturn';
import { centsToEuros } from '../../utils/moneyUtils';

/** Belegzeile für den Drilldown (GoBD-Geist, §5.7). */
export interface DrilldownEntry {
  id: string;
  dateISO: string;
  grossCents: number;
  periodDateIsFallback: boolean;
}

export interface VatReturnViewProps {
  figures: VatReturnFigures;
  /** Anzahl in der Periode erfasster Belege ohne BT-72 (unsicherer Datums-Fallback). */
  fallbackDateCount: number;
  /** In der Periode erfasste Einnahmen (belegbezogener Drilldown). */
  drilldown: DrilldownEntry[];
}

// ─── Geld-Formatierung (Darstellungsrundung erst hier, §5.8) ─────────────────────

const euroCentsFmt = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
});
const euroFullFmt = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Bemessungsgrundlage: auf volle Euro, Centbeträge ABGESCHNITTEN Richtung 0
 * (ELSTER-Konvention „volle Euro, Cent weglassen"). `Math.trunc` statt `Math.round`
 * (M-U12, §6.2): die Anzeige muss exakt die Export-Kürzung (`intdiv` serverseitig)
 * spiegeln — sonst könnten Vorschau und erzeugte Datei um ±1 € abweichen.
 */
function fmtBase(cents: number): string {
  return euroFullFmt.format(Math.trunc(cents / 100));
}

/** Steuer-/Zahllast-Betrag: Euro mit Cent. */
function fmtVat(cents: number): string {
  return euroCentsFmt.format(centsToEuros(cents));
}

// ─── KZ-Zeile ────────────────────────────────────────────────────────────────────

interface KzRowProps {
  /** Kennziffer-Label-Key (z. B. 'kz81'). */
  labelKey: string;
  baseCents?: number;
  vatCents?: number;
}

const KzRow: React.FC<KzRowProps & { t: (k: string) => string }> = ({
  labelKey,
  baseCents,
  vatCents,
  t,
}) => (
  <div className="flex items-center justify-between py-1.5 text-sm border-b border-hairline last:border-0">
    <span className="text-secondary">{t(`kz.${labelKey}`)}</span>
    <span className="flex items-center gap-4 tabular-nums">
      {baseCents !== undefined && (
        <span className="text-secondary min-w-[6rem] text-right">{fmtBase(baseCents)}</span>
      )}
      {vatCents !== undefined && (
        <span className="text-primary font-medium min-w-[6rem] text-right">{fmtVat(vatCents)}</span>
      )}
    </span>
  </div>
);

// ─── Gruppen-Karte ───────────────────────────────────────────────────────────────

interface GroupCardProps {
  titleKey: string;
  rows: KzRowProps[];
  t: (k: string) => string;
}

/** Rendert eine KZ-Gruppe als Karte; nur wenn mindestens ein Wert ≠ 0. */
const GroupCard: React.FC<GroupCardProps> = ({ titleKey, rows, t }) => {
  const hasValue = rows.some((r) => (r.baseCents ?? 0) !== 0 || (r.vatCents ?? 0) !== 0);
  if (!hasValue) return null;
  return (
    <div className="rounded-lg border border-hairline bg-surface-raised p-4">
      <h3 className="text-sm font-semibold text-accent-ink mb-2">{t(titleKey)}</h3>
      <div className="flex items-center justify-end gap-4 text-xs text-faint pb-1">
        <span className="min-w-[6rem] text-right">{t('columns.base')}</span>
        <span className="min-w-[6rem] text-right">{t('columns.vat')}</span>
      </div>
      {rows.map((r) => (
        <KzRow key={r.labelKey} {...r} t={t} />
      ))}
    </div>
  );
};

// ─── Hauptkomponente ───────────────────────────────────────────────────────────────

/**
 * Stellt die UStVA-Vorschau dar. Total/idempotent gegenüber den Props — keine Seiten-
 * effekte, kein Datenladen. §19-Sonderfall und Disclaimer/Scope sind Pflicht (§5.4/§5.6).
 */
const VatReturnView: React.FC<VatReturnViewProps> = ({
  figures,
  fallbackDateCount,
  drilldown,
}) => {
  const { t } = useTranslation('ustva');
  const [drilldownOpen, setDrilldownOpen] = useState(false);

  const f = figures;
  const unclassified = f.unclassifiedIncome;

  return (
    <div className="mt-6 space-y-4">
      {/* Haftungsausschluss (PFLICHT, §5.6) — OP-4 Jurist-Freigabe 2026-07-18. */}
      <div className="rounded-lg border border-[var(--tone-warning-border)] bg-[var(--tone-warning-bg)] p-4">
        <div className="flex items-start gap-2">
          <span className="text-[var(--tone-warning-fg)] mt-0.5">
            <IconWarning size={14} />
          </span>
          <div className="text-sm text-[var(--tone-warning-fg)]">
            <p className="font-semibold">{t('disclaimer.title')}</p>
            <p className="mt-1 text-[var(--tone-warning-fg)]">{t('disclaimer.body')}</p>
            <p className="mt-1 text-[var(--tone-warning-fg)]">{t('disclaimer.responsibility')}</p>
          </div>
        </div>
      </div>

      {/* §19-Sonderfall (§5.4) */}
      {f.smallBusiness ? (
        f.smallBusinessHasReverseChargeDuty ? (
          <>
            <div className="rounded-lg border border-[var(--tone-accent-border)] bg-[var(--tone-accent-bg)] p-3 text-sm text-[var(--tone-accent-fg)] flex items-start gap-2">
              <span className="text-accent-ink mt-0.5">
                <IconInfo size={14} />
              </span>
              <span>{t('smallBusiness.reverseChargeOnlyNotice')}</span>
            </div>
            <GroupCard
              titleKey="groups.inputOwed"
              rows={[
                { labelKey: 'kz46', baseCents: f.kz46BaseCents, vatCents: f.kz47VatCents },
                { labelKey: 'kz84', baseCents: f.kz84BaseCents, vatCents: f.kz85VatCents },
                { labelKey: 'kz89', baseCents: f.kz89BaseCents, vatCents: f.kz89VatCents },
                { labelKey: 'kz93', baseCents: f.kz93BaseCents, vatCents: f.kz93VatCents },
              ]}
              t={t}
            />
          </>
        ) : (
          <div className="rounded-lg border border-hairline bg-surface-raised p-4 text-sm text-secondary flex items-start gap-2">
            <span className="text-accent-ink mt-0.5">
              <IconInfo size={14} />
            </span>
            <span>{t('smallBusiness.notice')}</span>
          </div>
        )
      ) : (
        <>
          <GroupCard
            titleKey="groups.outputTaxable"
            rows={[
              { labelKey: 'kz81', baseCents: f.kz81BaseCents, vatCents: f.kz81VatCents },
              { labelKey: 'kz86', baseCents: f.kz86BaseCents, vatCents: f.kz86VatCents },
              { labelKey: 'kz35', baseCents: f.kz35BaseCents, vatCents: f.kz36VatCents },
            ]}
            t={t}
          />
          <GroupCard
            titleKey="groups.outputExempt"
            rows={[
              { labelKey: 'kz41', baseCents: f.kz41BaseCents },
              { labelKey: 'kz43', baseCents: f.kz43BaseCents },
              { labelKey: 'kz48', baseCents: f.kz48BaseCents },
              { labelKey: 'kz60', baseCents: f.kz60BaseCents },
            ]}
            t={t}
          />
          <GroupCard
            titleKey="groups.inputOwed"
            rows={[
              { labelKey: 'kz46', baseCents: f.kz46BaseCents, vatCents: f.kz47VatCents },
              { labelKey: 'kz84', baseCents: f.kz84BaseCents, vatCents: f.kz85VatCents },
              { labelKey: 'kz89', baseCents: f.kz89BaseCents, vatCents: f.kz89VatCents },
              { labelKey: 'kz93', baseCents: f.kz93BaseCents, vatCents: f.kz93VatCents },
            ]}
            t={t}
          />
          <GroupCard
            titleKey="groups.inputDeductible"
            rows={[
              { labelKey: 'kz66', vatCents: f.kz66VatCents },
              { labelKey: 'kz61', vatCents: f.kz61VatCents },
              { labelKey: 'kz67', vatCents: f.kz67VatCents },
            ]}
            t={t}
          />
        </>
      )}

      {/* Zahllast / Überschuss KZ 83 — prominent. */}
      <div className="rounded-lg border-2 border-indigo-500 bg-[var(--tone-accent-bg)] p-4 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-[var(--tone-accent-fg)]">{t('kz.kz83')}</p>
          <p className="text-xs text-accent-ink mt-0.5">
            {f.kz83PayableCents >= 0 ? t('result.payable') : t('result.refund')}
          </p>
        </div>
        <p
          className={`text-2xl font-bold tabular-nums ${
            f.kz83PayableCents >= 0 ? 'text-[var(--tone-accent-fg)]' : 'text-[var(--tone-success-fg)]'
          }`}
        >
          {fmtVat(f.kz83PayableCents)}
        </p>
      </div>

      {/* Fail-closed Bucket: unklassifizierte Einnahmen (§3.5) — sichtbare Warnung. */}
      {unclassified.count > 0 && (
        <div className="rounded-lg border border-[var(--tone-warning-border)] bg-[var(--tone-warning-bg)] p-3 text-sm text-[var(--tone-warning-fg)] flex items-start gap-2">
          <span className="text-[var(--tone-warning-fg)] mt-0.5">
            <IconWarning size={14} />
          </span>
          <span>
            {t('unclassifiedHint', {
              count: unclassified.count,
              amount: fmtBase(unclassified.grossCents),
            })}
          </span>
        </div>
      )}

      {/* VETO-1 (§7.4): eingangsseitig unklassifizierte Ausgaben (z. B. Drittland-EUSt,
          needs_review) — sichtbare Warnung analog zum Einnahmen-Bucket. PFLICHT, damit die
          Vorschau (Free/Premium, ohne Export-Flag) nicht stiller ist als der Export; ohne
          diesen Hinweis bliebe der EUSt-Verlust unsichtbar (Kz83 zu hoch). */}
      {f.unclassifiedExpense.count > 0 && (
        <div className="rounded-lg border border-[var(--tone-warning-border)] bg-[var(--tone-warning-bg)] p-3 text-sm text-[var(--tone-warning-fg)] flex items-start gap-2">
          <span className="text-[var(--tone-warning-fg)] mt-0.5">
            <IconWarning size={14} />
          </span>
          <span>
            {t('unclassifiedExpenseHint', {
              count: f.unclassifiedExpense.count,
              amount: fmtBase(f.unclassifiedExpense.grossCents),
            })}
          </span>
        </div>
      )}

      {/* GA-Freischaltung (jurist, § 13 Abs. 1 Nr. 1 Buchst. a UStG): dauerhafter Hinweis,
          dass Anzahlungs-/Abschlagsrechnungen NICHT in der Vorschau enthalten sind — sonst
          würde die Zahllast zu niedrig angesetzt. Statisch (keine Zähllogik), immer sichtbar
          außer beim Kleinunternehmer (dort keine USt-Zahllast). */}
      {!f.smallBusiness && (
        <div className="rounded-lg border border-hairline bg-surface-raised p-3 text-sm text-secondary flex items-start gap-2">
          <span className="text-[var(--tone-warning-fg)] mt-0.5">
            <IconInfo size={14} />
          </span>
          <span>{t('anzahlungHint')}</span>
        </div>
      )}

      {/* A1: Entwurfs-Belege mit realer §13b/Erwerb-Schuld (nicht in den Summen). */}
      {f.excludedDraftVatOwedCount > 0 && (
        <div className="rounded-lg border border-hairline bg-surface-raised p-3 text-sm text-secondary flex items-start gap-2">
          <span className="text-[var(--tone-warning-fg)] mt-0.5">
            <IconInfo size={14} />
          </span>
          <span>{t('draftOwedHint', { count: f.excludedDraftVatOwedCount })}</span>
        </div>
      )}

      {/* Soll-Fallback: Belege ohne BT-72-Leistungsdatum (unsicheres Periodenkriterium). */}
      {fallbackDateCount > 0 && (
        <div className="rounded-lg border border-hairline bg-surface-raised p-3 text-sm text-secondary flex items-start gap-2">
          <span className="text-[var(--tone-warning-fg)] mt-0.5">
            <IconInfo size={14} />
          </span>
          <span>{t('fallbackDateHint', { count: fallbackDateCount })}</span>
        </div>
      )}

      {/* Drilldown: zugrunde liegende Belege (GoBD-Geist, §5.7). */}
      {!f.smallBusiness && drilldown.length > 0 && (
        <div className="rounded-lg border border-hairline bg-surface-raised">
          <button
            type="button"
            onClick={() => setDrilldownOpen((o) => !o)}
            className="w-full flex items-center justify-between p-3 text-sm text-secondary"
            aria-expanded={drilldownOpen}
          >
            <span className="flex items-center gap-2">
              <span className="text-accent-ink">
                {drilldownOpen ? <IconChevronDown /> : <IconChevronRight />}
              </span>
              {t('drilldown.title', { count: drilldown.length })}
            </span>
          </button>
          {drilldownOpen && (
            <div className="px-3 pb-3">
              <div className="flex items-center justify-between text-xs text-faint pb-1 border-b border-hairline">
                <span>{t('drilldown.date')}</span>
                <span>{t('drilldown.gross')}</span>
              </div>
              {drilldown.map((d) => (
                <div
                  key={d.id}
                  className="flex items-center justify-between py-1.5 text-sm border-b border-hairline last:border-0"
                >
                  <span className="text-secondary">
                    {d.dateISO}
                    {d.periodDateIsFallback && (
                      <span className="ml-2 text-[var(--tone-warning-fg)] text-xs">{t('drilldown.fallbackTag')}</span>
                    )}
                  </span>
                  <span className="text-secondary tabular-nums">{fmtVat(d.grossCents)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Scope-Liste (PFLICHT, §5.6) — nicht abgebildete Sonderfälle. OP-4 Jurist-Freigabe 2026-07-18. */}
      <div className="rounded-lg border border-hairline bg-[rgb(var(--surface-raised-rgb)/0.6)] p-4">
        <h3 className="text-sm font-semibold text-secondary mb-2">{t('scope.title')}</h3>
        <p className="text-xs text-muted mb-2">{t('scope.intro')}</p>
        <ul className="list-disc list-inside text-xs text-muted space-y-0.5">
          <li>{t('scope.items.elster')}</li>
          <li>{t('scope.items.unentgeltlicheWertabgabe')}</li>
          <li>{t('scope.items.correction17')}</li>
          <li>{t('scope.items.advancePayments')}</li>
          <li>{t('scope.items.correction15a')}</li>
          <li>{t('scope.items.margin25a')}</li>
          <li>{t('scope.items.travel25')}</li>
          <li>{t('scope.items.farming24')}</li>
          <li>{t('scope.items.partialDeduction')}</li>
          <li>{t('scope.items.triangular25b')}</li>
          <li>{t('scope.items.reverseChargeCheck')}</li>
          <li>{t('scope.items.importVat')}</li>
          <li>{t('scope.items.section14c')}</li>
          <li>{t('scope.items.permanentExtension')}</li>
        </ul>
      </div>
    </div>
  );
};

export default VatReturnView;
